/**
 * Question Helpers - Shared Utilities for Question Preview
 *
 * Helper functions for transforming admin form data into Team objects
 * for use with frontend question components.
 */

import { Team } from './jersey-resolver';

/**
 * Option data structure for admin question previews
 *
 * This interface represents the option fields available in the admin form
 * that can be used to construct preview components.
 */
export interface OptionData {
  option_text_en: string;
  option_text_fr?: string;
  option_image_en?: string | null;
  option_image_fr?: string | null;
  third_image_en?: string | null; // Secondary/jersey image
  third_image_fr?: string | null;
  is_correct: boolean;
  points?: number;
}

/**
 * Mock color palette for consistent team styling in previews
 * Colors are from DraftKings brand palette
 */
export const MOCK_COLORS = [
  { color: '#c8102e', accent: '#fff' }, // Red
  { color: '#003f7f', accent: '#fff' }, // Blue
  { color: '#00ff88', accent: '#000' }, // Green
  { color: '#ff6b00', accent: '#fff' }, // Orange
] as const;

/**
 * Creates a Team object from admin option data
 *
 * Converts admin form option data into the Team interface expected by
 * frontend components (TeamPick, PlayerPick, PropPick).
 *
 * @param option - The option data from admin form
 * @param index - The option index (used for color selection)
 * @param language - Display language ('en' or 'fr')
 * @returns Team object ready for frontend components
 *
 * @example
 * const team = createTeamFromOption(
 *   { option_text_en: 'Lakers', option_image_en: '/lakers.png', is_correct: false },
 *   0,
 *   'en'
 * );
 * // Returns: { id: 'option-0', name: 'Lakers', jerseyUrl: '/lakers.png', ... }
 */
export function createTeamFromOption(
  option: OptionData,
  index: number,
  language: 'en' | 'fr' = 'en'
): Team {
  const colorPair = MOCK_COLORS[index % MOCK_COLORS.length];
  const textField = language === 'en' ? 'option_text_en' : 'option_text_fr';
  const imageField = language === 'en' ? 'option_image_en' : 'option_image_fr';
  const thirdImageField = language === 'en' ? 'third_image_en' : 'third_image_fr';

  return {
    id: `option-${index}`,
    name: option[textField] || option.option_text_en, // Fallback to English if French not available
    color: colorPair.color,
    accent: colorPair.accent,
    jerseyUrl: option[imageField],
    thirdImageUrl: option[thirdImageField],
    // popularityPct not available in admin preview (would come from API)
  };
}

/**
 * Creates an array of Team objects from admin options array
 *
 * Convenience function for creating multiple teams at once.
 *
 * @param options - Array of option data from admin form
 * @param language - Display language ('en' or 'fr')
 * @returns Array of Team objects
 */
export function createTeamsFromOptions(
  options: OptionData[],
  language: 'en' | 'fr' = 'en'
): Team[] {
  return options.map((option, index) => createTeamFromOption(option, index, language));
}
